import { pick, assoc, mergeRight, clamp, find, head, path, forEach, filter, propEq, chain } from 'ramda';
import WebMercatorViewport from 'viewport-mercator-project';
import { Functional as F, Wayfinding, WayfindingSegment } from 'vms-base';
/*
 * Types:
 *
 *   Action = { eventType: String, entityId: String, mapNoteId: Number }
 */
// ---------------------------------------------------------------------------------------------------------------------
//
// Constants
//
// ---------------------------------------------------------------------------------------------------------------------
const NAV_LINE_SOURCE = 'navlineSource';
const MAP_NOTES_SOURCE = 'mapNotes';
/* ---------------------------------------------------------------------------------------------------------------------
 *
 * Computations
 *
 * Types:
 *
 *  Viewport    = { latitude: Number, longitude: Number, ... }
 *  BoundingBox = { n: Number, e: Number, s: Number, w: Number }
 ---------------------------------------------------------------------------------------------------------------------*/
/*
 * Return a Mapbox viewport with lat/lng/zoom set to show the boundsPolygon.
 * This code uses built-in ReactMapGL dependency WebMercatorViewport;
 * @see the FAQ at https://uber.github.io/react-map-gl/#/Documentation/api-reference/static-map?section=methods
 * @sig zoomToBoundingBox :: (Viewport, BoundingBox, Number) -> Viewport
 */
const zoomToBoundingBox = (oldViewport, boundingBox, TRANSITION_DURATION) => {
    const { n, w, s, e } = boundingBox;
    let bounds = new WebMercatorViewport({ width: oldViewport.width, height: oldViewport.height })
        .fitBounds([[e, n], [w, s]], { padding: 40 });
    bounds = pick(['latitude', 'longitude', 'zoom'], bounds);
    bounds = assoc('transitionDuration', TRANSITION_DURATION, bounds);
    return mergeRight(oldViewport, bounds);
};
/*
 * Check bounding box for max panning
 * @sig clampBounds :: (Viewport, BoundingBox) -> Viewport
 */
const clampBounds = (viewport, boundingBox) => {
    if (viewport.latitude === 0 || viewport.longitude === 0 || !boundingBox)
        return viewport;
    const { n, s, e, w } = boundingBox;
    return mergeRight(viewport, {
        latitude: clamp(s, n, viewport.latitude),
        longitude: clamp(w, e, viewport.longitude),
    });
};
// ---------------------------------------------------------------------------------------------------------------------
//
// Determine action from feature click
//
// ---------------------------------------------------------------------------------------------------------------------
/*
 * Given an array of Features, select the "best" one
 * @sig prioritizeAmongFeatures :: [Feature] -> Feature
 */
const prioritizeAmongFeatures = F.condNotNil(find((f) => f.source === NAV_LINE_SOURCE), // top priority: nav badge
find((f) => f.source === MAP_NOTES_SOURCE), // second: mapNotes
find((f) => 'aiLayer' in f.properties && f.properties.aiLayer === 'poi'), // third: general poi
find((f) => 'category' in f.properties && f.properties.category === 'unoccupied'), // fourth: unoccupied features
head);
/*
 * Wrap an action for determineMapClick, merging an eventType with an optional extra value
 * :: (String, {k:v}) -> Action
 */
const wrapAction = (eventType, o = {}) => mergeRight({ eventType }, o);
/*
 * Determine whether the user clicked on an Entity, MapNote or something else
 * @sig determineActionFromFeature :: [MapboxFeature] -> Action
 */
const determineActionFromFeature = (mapboxFeatures) => {
    const rawFeature = prioritizeAmongFeatures(mapboxFeatures);
    const entityId = F.pathString('properties.id', rawFeature);
    const mapNoteId = F.pathString('properties.noteId', rawFeature);
    const isPoi = F.pathStringEq('properties.aiLayer', 'poi', rawFeature);
    const isUnoccupied = F.pathStringEq('properties.category', 'unoccupied', rawFeature);
    if (entityId && isUnoccupied)
        return wrapAction('EDIT_ENTITY', { entityId, rawFeature });
    if (entityId && isPoi)
        return wrapAction('EDIT_ENTITY', { entityId });
    if (mapNoteId)
        return wrapAction('EDIT_MAP_NOTE', { mapNoteId });
    return wrapAction('NOTHING_CLICKED');
};
/*
 * Return an "action" based on the mode or what the user clicked on
 * @sig determineActionFromFeature :: [MapboxFeature] -> Action
 */
const determineMapClickAction = (mode, mapboxFeatures) => {
    if (mode.poiClick)
        return wrapAction('ADD_ENTITY');
    if (mode.noteClick)
        return wrapAction('ADD_MAP_NOTE');
    if (mode.movePoiMode)
        return wrapAction('MOVE_ENTITY');
    return determineActionFromFeature(mapboxFeatures);
};
// ---------------------------------------------------------------------------------------------------------------------
//
// Navigation
// HACK/DEMO: select one of two hard-coded routes as if the user had really navigated
//
// ---------------------------------------------------------------------------------------------------------------------
let currentRouteIndex = 2;
const mockRoutes = [
    { startLocation: 'The Front Porch', endLocation: 'Gate 41' },
    { startLocation: 'The Front Porch', endLocation: 'AA Baggage' },
    { startLocation: '', endLocation: '' },
];
// hack: return the next route in our very short list of mocked routes
const incrementRoute = () => {
    currentRouteIndex = (currentRouteIndex + 1) % mockRoutes.length;
    return mockRoutes[currentRouteIndex];
};
/*
 * Return data for a theoretical navigation
 * @sig () -> { startLocation: String, endLocation: String, segments: [WayfindingSegment] }
 */
const navigate = () => {
    const { startLocation, endLocation } = incrementRoute();
    return {
        startLocation,
        endLocation,
        segments: Wayfinding.navigate(startLocation, endLocation),
    };
};
/*
 * Convert WayfindingSegments to GeoJSON
 * @sig navlineFeatures :: State -> [GeoJsonFeature]
 */
const navlineFeatures = (state) => {
    const { currentOrdinalId } = state;
    const segments = filter(propEq(currentOrdinalId, 'ordinalId'), state.wayfinding.segments);
    return chain(WayfindingSegment.toGeoJson, segments);
};
// ---------------------------------------------------------------------------------------------------------------------
//
// Validation
//
// ---------------------------------------------------------------------------------------------------------------------
const layerWarning = (feature, layerType, geometryType) => {
    const layerId = F.pathString('layer.id', feature);
    const venueId = F.pathString('properties.venueId', feature);
    const id = F.pathString('properties.id', feature);
    const subid = F.pathString('properties.subid', feature);
    return `${layerType} layer '${layerId}' has incorrect geometry type: ${geometryType} `
        + `for venue: ${venueId} id: ${id} subid: ${subid}`;
};
/*
 * Only certain types of geometry should appear on a given layer, or the style.json was wrong
 * @sig geometryMatchesLayer :: (String, String) -> Boolean
 */
const geometryMatchesLayer = (layer, geometry) => {
    switch (layer) {
        case 'symbol': return geometry === 'Point';
        case 'fill': return geometry === 'Polygon' || geometry === 'MultiPolygon';
        case 'line': return geometry === 'LineString' || geometry === 'Polygon' || geometry === 'MultiLineString';
        default: return true;
    }
};
/*
 * Log if this feature isn't appropriate for the layer it's onkA
 * impure: write to console
 * @sig validateFeatureOnLayer :: Feature -> ()
 *
 */
const validateFeatureOnLayer = feature => {
    const layerType = path(['layer', 'type'], feature);
    const geometryType = path(['geometry', 'type'], feature);
    if (!geometryMatchesLayer(layerType, geometryType))
        console.error(layerWarning(feature, layerType, geometryType));
};
const validateFeaturesOnLayer = forEach(validateFeatureOnLayer);
export { NAV_LINE_SOURCE, MAP_NOTES_SOURCE, 
// validation
validateFeaturesOnLayer, 
// navigation
navigate, navlineFeatures, 
// map click
determineMapClickAction, 
// computations
zoomToBoundingBox, clampBounds, };
